import type { ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * Pill primitives — shared by the reader/memorize/search translation +
 * language toggles AND by surah-index filters.
 *
 * Visual idiom: cyan "beam" active fill on a dark night-themed surface.
 * Pills are SEPARATE (no surrounding glass container), matching the
 * existing SurahIndex filter cluster so reader and index feel like one
 * system.
 *
 * Three exports:
 *   <TranslationPillGroup>      — flex row, no background of its own
 *   <TranslationPill>           — one pill (active beam / inactive outline / optional dot)
 *   <TranslationPillSeparator>  — faint vertical divider between sections
 *
 * Pure presentation. State (selected value, on-change) lives in the
 * consuming toggle component.
 */

export function TranslationPillGroup({
  children,
  ariaLabel,
  className,
}: {
  children: ReactNode;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        // No bg / no border / no padding — pills float side by side.
        // gap-2 matches the SurahIndex filter cluster so reader and
        // index visually align.
        'inline-flex items-center gap-2 whitespace-nowrap',
        className,
      )}
    >
      {children}
    </div>
  );
}

type PillProps = {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  ariaLabel?: string;
  title?: string;
  /** Prefix the label with a small dot (used to signal switch-type pills). */
  dot?: boolean;
  disabled?: boolean;
  /**
   * Role override. Defaults to "radio" (inside a radio group). For an
   * on/off toggle, pass "switch".
   */
  role?: 'radio' | 'switch';
};

export function TranslationPill({
  active,
  onClick,
  children,
  ariaLabel,
  title,
  dot = false,
  disabled = false,
  role = 'radio',
}: PillProps) {
  const ariaProps =
    role === 'switch'
      ? { 'aria-checked': active, role: 'switch' as const }
      : { 'aria-checked': active, role: 'radio' as const };

  return (
    <button
      type="button"
      {...ariaProps}
      aria-label={ariaLabel}
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        // Match SurahIndex filter pill — uppercase, tracked, larger
        // hit area. One canonical pill style across reader + index +
        // memorize + search so the app reads as one system.
        'inline-flex items-center gap-1.5 leading-none',
        'rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide',
        'transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-beam/40',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        active
          ? 'bg-beam text-beam-ink shadow-sm'
          : cn(
              'border border-night-500/40 bg-night-800 text-night-200',
              'hover:border-beam/50 hover:text-beam-pale',
            ),
      )}
    >
      {dot && (
        <span
          aria-hidden
          className={cn(
            'inline-block h-1.5 w-1.5 rounded-full shrink-0',
            // On a beam-filled pill, use the ink color so the dot is visible.
            // On an outline pill, use beam itself so it reads as an accent.
            active ? 'bg-beam-ink' : 'bg-beam',
          )}
        />
      )}
      <span className="inline-block leading-none">{children}</span>
    </button>
  );
}

export function TranslationPillSeparator() {
  return (
    <span
      aria-hidden
      // Faint vertical divider for use inside a ReaderToolbar where
      // multiple groups of pills sit on one row.
      className="inline-block h-4 w-px bg-night-500/30 mx-0.5"
    />
  );
}
